/**
 * Notifications context. Loads the signed-in user's notifications from the API
 * and appends live ones that arrive over the shared WebSocket `notify` message.
 * The nav bell reads `unreadCount`; the notifications drawer reads `items`.
 *
 * Backend endpoints degrade gracefully: if the notifications API is not wired up
 * yet, the initial load simply yields an empty list and live `notify` messages
 * still show.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { SERVER_MSG, type NotificationDto } from '@proteam/shared';

import { api } from './api.js';
import { getWs } from './ws.js';

interface NotificationsApi {
  items: NotificationDto[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  refresh: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsApi | null>(null);

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<NotificationDto[]>([]);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const list = await api.get<NotificationDto[]>('/accounts/notifications');
      if (mounted.current && Array.isArray(list)) setItems(list);
    } catch {
      // Not configured yet, or a transient error: keep whatever we already have.
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const ws = getWs();
    ws.connect();
    void refresh();

    const off = ws.on(SERVER_MSG.notify, (msg) => {
      const dto: NotificationDto = {
        id: newId(),
        kind: msg.payload.kind,
        body: msg.payload.body,
        href: msg.payload.href ?? null,
        readAt: null,
        createdAt: new Date().toISOString(),
      };
      setItems((list) => [dto, ...list].slice(0, 100));
    });

    return () => {
      mounted.current = false;
      off();
    };
  }, [refresh]);

  const markRead = useCallback((id: string) => {
    setItems((list) =>
      list.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)),
    );
    void api.patch('/accounts/notifications/read', { ids: [id] }).catch(() => undefined);
  }, []);

  const markAllRead = useCallback(() => {
    const now = new Date().toISOString();
    setItems((list) => list.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    void api.patch('/accounts/notifications/read', { all: true }).catch(() => undefined);
  }, []);

  const unreadCount = useMemo(() => items.filter((n) => !n.readAt).length, [items]);

  const value = useMemo<NotificationsApi>(
    () => ({ items, unreadCount, markRead, markAllRead, refresh }),
    [items, unreadCount, markRead, markAllRead, refresh],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsApi {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used inside a NotificationsProvider');
  return ctx;
}
